import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { Eye, Users, Download, ArrowLeft, Globe, Monitor, Smartphone, BarChart3, FileText, MapPin, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { api } from "../lib/api";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
// ip-api country name -> world-atlas properties.name
const NAME_FIX = {
  "United States": "United States of America",
  "Czechia": "Czech Republic",
  "Republic of Korea": "South Korea",
  "Russia": "Russia",
  "Tanzania": "United Republic of Tanzania",
  "Serbia": "Republic of Serbia",
  "Dominican Republic": "Dominican Rep.",
  "Bosnia and Herzegovina": "Bosnia and Herz.",
};
const norm = (name) => NAME_FIX[name] || name;

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

const PERIODS = [{ d: 7, l: "7 giorni" }, { d: 30, l: "30 giorni" }, { d: 90, l: "90 giorni" }];

function DeltaBadge({ delta }) {
  if (delta === null || delta === undefined) {
    return <p data-testid="delta-badge" className="text-[11px] font-mono text-muted-foreground mt-1">— vs periodo precedente</p>;
  }
  const up = delta >= 0;
  const Icon = delta === 0 ? Minus : up ? TrendingUp : TrendingDown;
  const color = delta === 0 ? "#8b95a5" : up ? "#39FF14" : "#FF3B6B";
  return (
    <p data-testid="delta-badge" className="text-[11px] font-mono mt-1 flex items-center gap-1" style={{ color }}>
      <Icon className="w-3 h-3" /> {up && delta !== 0 ? "+" : ""}{delta}% <span className="text-muted-foreground">vs periodo precedente</span>
    </p>
  );
}

function Breakdown({ title, icon: Icon, items, color, testid }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div data-testid={testid} className="p-5 bg-card border border-white/10 rounded-sm">
      <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2"><Icon className="w-4 h-4" style={{ color }} /> {title}</h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground font-light">—</p> : (
        <div className="space-y-2.5">
          {items.map((it, i) => (
            <div key={i}>
              <div className="flex justify-between text-xs mb-1"><span className="text-white truncate max-w-[70%]">{it.label}</span><span className="font-mono text-muted-foreground">{it.count}</span></div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${(it.count / max) * 100}%`, backgroundColor: color }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VisitorMap({ countries }) {
  const [hover, setHover] = useState(null);
  const counts = {};
  countries.forEach((c) => { counts[norm(c.label)] = c.count; });
  const max = Math.max(1, ...countries.map((c) => c.count));
  const color = scaleLinear().domain([0, max]).range(["#12324a", "#00F0FF"]);
  return (
    <div className="p-6 bg-card border border-white/10 rounded-sm" data-testid="stats-map">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2"><MapPin className="w-4 h-4 text-neon-cyan" /> Mappa visitatori</h3>
        <span className="font-mono text-xs text-neon-cyan h-5">{hover ? `${hover.name}: ${hover.count} visite` : ""}</span>
      </div>
      {countries.length === 0 && (
        <p className="text-xs text-muted-foreground font-light mb-3">Nessun dato geografico ancora nel periodo selezionato: la mappa si colorerà man mano che arrivano visite.</p>
      )}
      <div className="rounded-sm overflow-hidden bg-[#0a0e14]" data-testid="stats-map-svg">
        <ComposableMap projectionConfig={{ scale: 130 }} height={360} style={{ width: "100%", height: "auto" }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const val = counts[name] || 0;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => setHover({ name, count: val })}
                    onMouseLeave={() => setHover(null)}
                    style={{
                      default: { fill: val > 0 ? color(val) : "#1a2230", stroke: "#0a0e14", strokeWidth: 0.4, outline: "none" },
                      hover: { fill: "#FF007F", stroke: "#0a0e14", strokeWidth: 0.4, outline: "none" },
                      pressed: { fill: "#FF007F", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>
    </div>
  );
}

export default function StatsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    api.get("/analytics", { params: { days } })
      .then((r) => { if (reqIdRef.current === myId) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (reqIdRef.current === myId) setLoading(false); });
  }, [days]);

  const cards = data ? [
    { label: "Visite totali", value: data.total_views, color: "#00F0FF", icon: Eye, delta: data.deltas?.views },
    { label: "Visitatori unici", value: data.unique_visitors, color: "#39FF14", icon: Users, delta: data.deltas?.unique },
    { label: "Installazioni app", value: data.total_installs, color: "#FF007F", icon: Download, delta: data.deltas?.installs },
  ] : [];
  const maxDay = data ? Math.max(1, ...data.views_by_day.map((d) => d.views)) : 1;

  return (
    <div className="noise-overlay min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <Link to="/admin" data-testid="stats-back" className="inline-flex items-center gap-2 text-sm font-mono text-muted-foreground hover:text-neon-cyan transition-colors duration-200"><ArrowLeft className="w-4 h-4" /> Torna alla Dashboard</Link>
        <div className="mt-3 mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-neon-green">Solo amministratore</span>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white mt-1">Statistiche</h1>
            <p className="text-muted-foreground font-light mt-2 text-sm">Afflusso di visite, installazioni e provenienza dei visitatori (dati anonimi).</p>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-sm bg-card border border-white/10 self-start" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button key={p.d} data-testid={`period-${p.d}`} onClick={() => setDays(p.d)}
                className={`px-3 h-9 rounded-sm text-sm font-semibold transition-colors duration-200 ${days === p.d ? "bg-neon-cyan text-black" : "text-muted-foreground hover:text-white"}`}>
                {p.l}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="font-mono text-neon-cyan animate-pulse-glow" data-testid="stats-loading">Caricamento…</p>
        ) : !data ? (
          <p className="text-muted-foreground">Nessun dato disponibile.</p>
        ) : (
          <div className="space-y-8" data-testid="stats-content">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {cards.map((c, i) => (
                <div key={i} data-testid={`stats-card-${i}`} className="p-5 bg-card border border-white/10 rounded-sm relative overflow-hidden">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground"><c.icon className="w-4 h-4" style={{ color: c.color }} /> {c.label}</div>
                  <div className="font-display font-black text-4xl mt-2" style={{ color: c.color }}>{c.value}</div>
                  <DeltaBadge delta={c.delta} />
                  <div className="absolute bottom-0 left-0 h-0.5 w-full" style={{ backgroundColor: c.color, boxShadow: `0 0 10px ${c.color}` }} />
                </div>
              ))}
            </div>

            <div className="p-6 bg-card border border-white/10 rounded-sm">
              <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-5 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-neon-cyan" /> Visite per giorno ({days} giorni)</h3>
              <div className="flex items-end gap-1 h-40" data-testid="stats-chart">
                {data.views_by_day.map((d, i) => (
                  <div key={i} className="flex-1 h-full flex flex-col justify-end items-center" title={`${d.date}: ${d.views} visite`}>
                    <div className="w-full rounded-t-sm bg-neon-cyan/70 hover:bg-neon-cyan transition-colors duration-200" style={{ height: `${(d.views / maxDay) * 100}%`, minHeight: d.views > 0 ? "4px" : "0px" }} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-[10px] font-mono text-muted-foreground">
                <span>{data.views_by_day[0]?.date?.slice(5)}</span>
                <span>oggi</span>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2"><VisitorMap countries={data.countries || []} /></div>
              <Breakdown title="Paesi" icon={Globe} items={data.countries || []} color="#00F0FF" testid="stats-countries" />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <Breakdown title="Pagine più viste" icon={FileText} items={data.top_pages} color="#A855F7" testid="stats-pages" />
              <Breakdown title="Dispositivi" icon={Smartphone} items={data.devices} color="#39FF14" testid="stats-devices" />
              <Breakdown title="Browser" icon={Monitor} items={data.browsers} color="#00F0FF" testid="stats-browsers" />
            </div>

            <div className="p-6 bg-card border border-white/10 rounded-sm">
              <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2"><Globe className="w-4 h-4 text-neon-pink" /> Visite recenti (anonime)</h3>
              {data.recent.length === 0 ? (
                <p className="text-sm text-muted-foreground font-light">Ancora nessuna visita registrata nel periodo selezionato.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground border-b border-white/10">
                        <th className="py-2 pr-4">Quando</th><th className="py-2 pr-4">Luogo</th><th className="py-2 pr-4">Dispositivo</th><th className="py-2 pr-4">Browser</th><th className="py-2 pr-4">Pagina</th><th className="py-2">Provenienza</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent.map((v, i) => (
                        <tr key={i} data-testid={`stats-recent-${i}`} className="border-b border-white/5 text-muted-foreground">
                          <td className="py-2 pr-4 font-mono text-xs whitespace-nowrap">{fmtTime(v.created_at)}</td>
                          <td className="py-2 pr-4 text-white">{[v.city, v.country].filter(Boolean).join(", ") || "—"}</td>
                          <td className="py-2 pr-4">{v.device}</td>
                          <td className="py-2 pr-4">{v.browser}</td>
                          <td className="py-2 pr-4 font-mono text-xs text-neon-cyan">{v.path}</td>
                          <td className="py-2 font-mono text-xs truncate max-w-[180px]">{v.referrer || "Diretta"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
